import { Link, NavLink, Outlet, ScrollRestoration, useMatches } from "react-router-dom"
import { useState } from "react"
import { ModerationResult } from "~/__generated__/graphql"
import { useLogout } from "~/auth/use-logout"
import { useViewer, useViewerMaybe } from "~/auth/use-viewer"
import { UserAvatarWithFallback } from "~/common/avatar-with-fallback"
import { cn } from "~/common/cn"
import {
  adminAdminUsersPath,
  adminAuraProductsPath,
  adminAdSponsorsPath,
  adminDashboardPath,
  adminMessageModerationPath,
  adminRoomsPath,
  adminUsersPath,
  adminPredictionsPath,
  adminRambleMessagesPath,
  adminEarningsPlansPath,
  adminCacheStatsPath,
  adminRankingPath,
  adminLoadSheddingStatsPath,
  graphiqlPath,
  graphqlPath,
  featureFlagsPath,
  pgheroPath,
  sidekiqPath,
} from "~/common/paths"
import bubble from "~/images/bubble"
import home from "~/images/home"
import logoSm from "~/images/logo-sm"
import logoutIcon from "~/images/logout-icon"
import people from "~/images/people"
import userSquare from "~/images/user-square"

import { AppText } from "~/ui/app-text"

const sidebarItemStyles =
  "flex px-5 py-2 hover:bg-gray-100 text-sm font-medium text-gray-400 hover:text-gray-900 cursor-pointer"

const SIDEBAR_WIDTH = 200

const SidebarLink = ({
  text,
  to,
  icon,
  end,
  target,
}: {
  text: string
  to: string
  icon: React.ReactNode
  end?: boolean
  target?: React.HTMLAttributeAnchorTarget
}) => {
  return (
    <li className="">
      <NavLink
        to={to}
        className={({ isActive }) => {
          return cn(sidebarItemStyles, "flex", { "opacity-50": !isActive })
        }}
        end={end}
        target={target}
      >
        {icon}
        <AppText variant="h3" className="pl-4 uppercase">
          {text}
        </AppText>
      </NavLink>
    </li>
  )
}

export const SidebarLinks = () => {
  const { viewer } = useViewer()

  return (
    <nav className="mt-4">
      <ul className="list-none flex-col divide-y divide-gray-200 border-b border-t border-gray-200">
        {viewer.isAdmin && (
          <>
            <SidebarLink
              text="Home"
              to={adminDashboardPath({})}
              icon={<img {...home} />}
              end={true}
            />
            <SidebarLink
              text="Admin Users"
              to={adminAdminUsersPath({})}
              icon={<img {...userSquare} />}
            />
            <SidebarLink text="Users" to={adminUsersPath({})} icon={<img {...people} />} />
            <SidebarLink text="Chats" to={adminRoomsPath({})} icon={<img {...bubble} />} />
            <SidebarLink
              text="Predictions"
              to={adminPredictionsPath({})}
              icon={<img {...bubble} />}
            />
            <SidebarLink
              text="Ramble"
              to={adminRambleMessagesPath({})}
              icon={<img {...bubble} />}
            />
            <SidebarLink
              text="Earnings"
              to={adminEarningsPlansPath({})}
              icon={<img {...bubble} />}
            />
            <SidebarLink text="Aura" to={adminAuraProductsPath({})} icon={<img {...bubble} />} />
            <SidebarLink text="Ads" to={adminAdSponsorsPath({})} icon={<img {...bubble} />} />
            <SidebarLink
              text="Moderation"
              to={adminMessageModerationPath({ moderationResult: ModerationResult.Flagged })}
              icon={<img {...bubble} />}
            />
            <SidebarLink
              text="Feature Flags"
              to={featureFlagsPath.pattern}
              icon={<img {...bubble} />}
              target="_blank"
            />
            <SidebarLink
              text="Sidekiq"
              to={sidekiqPath.pattern}
              icon={<img {...bubble} />}
              target="_blank"
            />
            <SidebarLink
              text="PGHero"
              to={pgheroPath.pattern}
              icon={<img {...bubble} />}
              target="_blank"
            />
            <SidebarLink
              text="GraphQL"
              to={graphqlPath.pattern}
              icon={<img {...bubble} />}
              target="_blank"
            />
            <SidebarLink
              text="GraphiQL Docs"
              to={graphiqlPath.pattern}
              icon={<img {...bubble} />}
              target="_blank"
            />
            <SidebarLink text="Ranking" to={adminRankingPath({})} icon={<img {...bubble} />} />
            <SidebarLink
              text="Cache Stats"
              to={adminCacheStatsPath({})}
              icon={<img {...bubble} />}
            />
            <SidebarLink
              text="Load Shedding"
              to={adminLoadSheddingStatsPath({})}
              icon={<img {...bubble} />}
            />
          </>
        )}
      </ul>
    </nav>
  )
}

export const SidebarLayout = () => {
  const { viewer } = useViewerMaybe()
  const logout = useLogout()
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const matches = useMatches()
  const fullWidth = matches.some(
    (match) => (match.handle as { fullWidth?: boolean } | undefined)?.fullWidth
  )

  if (!viewer) {
    return null
  }

  const closeMobileMenu = () => setIsMobileMenuOpen(false)

  return (
    <div className="flex min-h-screen flex-1 grow">
      {/* Mobile menu button */}
      <button
        onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        className="fixed left-4 top-[calc(env(safe-area-inset-top)_+_1rem)] z-50 rounded-md bg-white p-2 shadow-md md:hidden"
        aria-label="Toggle menu"
      >
        <svg
          className="h-6 w-6 text-gray-600"
          fill="none"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          {isMobileMenuOpen ? (
            <path d="M6 18L18 6M6 6l12 12" />
          ) : (
            <path d="M4 6h16M4 12h16M4 18h16" />
          )}
        </svg>
      </button>

      {/* Overlay for mobile */}
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 z-30 bg-black bg-opacity-50 md:hidden"
          onClick={closeMobileMenu}
        />
      )}

      {/* Sidebar */}
      <div
        className={cn(
          "fixed top-0 z-40 flex h-dvh flex-col justify-between gap-2 overflow-auto bg-white pb-[env(safe-area-inset-bottom)] pt-[env(safe-area-inset-top)] shadow-lg transition-transform duration-300 ease-in-out",
          "md:translate-x-0",
          isMobileMenuOpen ? "translate-x-0" : "-translate-x-full"
        )}
        style={{ width: SIDEBAR_WIDTH }}
      >
        <div>
          <div className="flex justify-center">
            <Link to={adminDashboardPath({})} className="flex pt-6" onClick={closeMobileMenu}>
              <img {...logoSm} alt="ChatBCC" />
            </Link>
          </div>

          <div onClick={closeMobileMenu}>
            <SidebarLinks />
          </div>
        </div>
        <div>
          <div className="flex items-center gap-2 p-4 py-6">
            <UserAvatarWithFallback user={viewer} size="menu" />
            <AppText variant="body3-medium">{viewer.displayName || viewer.username}</AppText>
          </div>
          <div className={cn("border-t border-gray-200", sidebarItemStyles)}>
            <button
              onClick={() => {
                logout()
                closeMobileMenu()
              }}
              className="flex w-full items-center"
            >
              <img {...logoutIcon} alt="" />
              <AppText variant="h3" className="pl-4 uppercase opacity-50">
                Sign Out
              </AppText>
            </button>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="w-full md:ml-[200px]">
        <div
          className={cn(
            "mx-auto flex flex-1 flex-col overflow-hidden break-words px-10 pb-14 pt-[calc(env(safe-area-inset-top)_+_3.5rem)] [overflow-wrap:anywhere]",
            fullWidth ? "max-w-none" : "max-w-screen-xl"
          )}
        >
          <Outlet />
          <ScrollRestoration />
        </div>
      </div>
    </div>
  )
}
